import React, { useState, useEffect, useRef } from 'react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import './Header.css';

const LanguageSwitcherButton: React.FC = () => {
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasAnimated(true);
    }, 400); // Small delay for better effect
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = (languageCode: string) => {
    changeLanguage(languageCode);
    setIsOpen(false);
  };

  const currentLanguageName = languages.find(lang => lang.code === currentLanguage)?.nativeName || 'English';

  return (
    <div className={`user-actions language-switcher-container ${hasAnimated ? 'animated' : ''}`} ref={dropdownRef}>
      <button 
        className="action-button language-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('navigation.changeLanguage')}
      >
        <GlobeAltIcon  width={30} height={30} />
      </button>
      
      {isOpen && (
        <div className="language-switch-horizontal">
          <div className="language-switch-track">
            {languages.map((language) => (
              <button
                key={language.code}
                className={`language-switch-option ${currentLanguage === language.code ? 'active' : ''}`}
                onClick={() => handleLanguageChange(language.code)}
              >
                <span className="language-code">{language.code.toUpperCase()}</span>
              </button>
            ))}
            <div 
              className="language-switch-slider"
              style={{
                width: `calc(${100 / languages.length}% - 2px)`,
                transform: `translateX(${languages.findIndex(lang => lang.code === currentLanguage) * 100}%)`
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcherButton;

